import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import PageHeader from '../components/PageHeader';
import { Session } from '@supabase/supabase-js';
import { LogOut, Plus, Image, MessageCircle, Settings, CheckSquare, Home } from 'lucide-react';
import AlertDialog from '../components/AlertDialog';

// Import admin components
import GuestManagement from '../components/admin/GuestManagement';
import GiftManagement from '../components/admin/GiftManagement';
import PhotoManagement from '../components/admin/PhotoManagement';
import MessageManagement from '../components/admin/MessageManagement';
import SettingsManagement from '../components/admin/SettingsManagement';
import HomepageImageManagement from '../components/admin/HomepageImageManagement';

// Import custom hooks
import {
  useGuestManagement,
  useGiftManagement,
  usePhotoManagement,
  useMessageManagement,
  useSettingsManagement,
  useHomepageImageManagement,
  useAlertDialog
} from '../hooks';

type ActiveTab = 'guests' | 'gifts' | 'photos' | 'messages' | 'settings' | 'homepage';

const AdminPage: React.FC = () => {
  const navigate = useNavigate();
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [activeTab, setActiveTab] = useState<ActiveTab>('guests');

  // Custom hooks
  const {
    guests,
    loading: guestsLoading,
    editGuest,
    deleteGuest
  } = useGuestManagement();

  const {
    gifts,
    loading: giftsLoading,
    addGift,
    editGift,
    deleteGift
  } = useGiftManagement();

  const {
    photos,
    loading: photosLoading,
    deletePhoto
  } = usePhotoManagement();

  const {
    messages,
    loading: messagesLoading,
    toggleApproval,
    deleteMessage,
    approveAll
  } = useMessageManagement();

  const {
    photoAlbumUnlockDate,
    loading: settingsLoading,
    unlockPhotoAlbum,
    lockPhotoAlbum
  } = useSettingsManagement();

  const {
    images,
    loading: imagesLoading,
    error: imagesError,
    addImage,
    updateImage,
    deleteImage
  } = useHomepageImageManagement();

  const {
    showAlert,
    alertTitle,
    alertMessage,
    setAlert,
    clearAlert
  } = useAlertDialog();

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      setLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      setSession(session);
    });

    return () => subscription.unsubscribe();
  }, []);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      if (error) throw error;
    } catch (error) {
      console.error('Error logging in:', error);
      setAlert('Erro', 'Erro ao fazer login. Verifique suas credenciais.');
    }
  };

  const handleLogout = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      navigate('/');
    } catch (error) {
      console.error('Error logging out:', error);
      setAlert('Erro', 'Erro ao fazer logout. Por favor, tente novamente.');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-wedding-blue-600"></div>
      </div>
    );
  }

  if (!session) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-lg p-8 max-w-md w-full">
          <h2 className="text-2xl font-playfair font-semibold text-wedding-blue-800 mb-6 text-center">
            Área Administrativa
          </h2>
          
          <form onSubmit={handleLogin}>
            <div className="mb-4">
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-wedding-blue-500"
                required
              />
            </div>
            
            <div className="mb-6">
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                Senha
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-wedding-blue-500"
                required
              />
            </div>
            
            <button
              type="submit"
              className="w-full py-2 bg-wedding-blue-600 text-white rounded-md hover:bg-wedding-blue-700 transition-colors"
            >
              Entrar
            </button>
          </form>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen py-12 px-4">
      <div className="container mx-auto max-w-7xl">
        <div className="flex justify-between items-center mb-8">
          <PageHeader 
            title="Área Administrativa" 
            subtitle="Gerencie confirmações de presença e presentes"
          />
          
          <button
            onClick={handleLogout}
            className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors"
          >
            <LogOut size={18} />
            Sair
          </button>
        </div>
        
        <div className="flex justify-center mb-8">
          <div className="bg-white rounded-full shadow-md p-1">
            <button
              onClick={() => setActiveTab('guests')}
              className={`px-6 py-2 rounded-full transition-colors ${
                activeTab === 'guests'
                  ? 'bg-wedding-blue-600 text-white'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <CheckSquare size={18} className="inline-block mr-2" />
              Convidados
            </button>
            <button
              onClick={() => setActiveTab('gifts')}
              className={`px-6 py-2 rounded-full transition-colors ${
                activeTab === 'gifts'
                  ? 'bg-wedding-blue-600 text-white'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <Plus size={18} className="inline-block mr-2" />
              Presentes
            </button>
            <button
              onClick={() => setActiveTab('photos')}
              className={`px-6 py-2 rounded-full transition-colors ${
                activeTab === 'photos'
                  ? 'bg-wedding-blue-600 text-white'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <Image size={18} className="inline-block mr-2" />
              Fotos
            </button>
            <button
              onClick={() => setActiveTab('messages')}
              className={`px-6 py-2 rounded-full transition-colors ${
                activeTab === 'messages'
                  ? 'bg-wedding-blue-600 text-white'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <MessageCircle size={18} className="inline-block mr-2" />
              Mensagens
            </button>
            <button
              onClick={() => setActiveTab('homepage')}
              className={`px-6 py-2 rounded-full transition-colors ${
                activeTab === 'homepage'
                  ? 'bg-wedding-blue-600 text-white'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <Home size={18} className="inline-block mr-2" />
              Página Inicial
            </button>
            <button
              onClick={() => setActiveTab('settings')}
              className={`px-6 py-2 rounded-full transition-colors ${
                activeTab === 'settings'
                  ? 'bg-wedding-blue-600 text-white'
                  : 'text-gray-700 hover:bg-gray-100'
              }`}
            >
              <Settings size={18} className="inline-block mr-2" />
              Configurações
            </button>
          </div>
        </div>
        
        {activeTab === 'guests' && !guestsLoading && (
          <GuestManagement
            guests={guests}
            onEditGuest={editGuest}
            onDeleteGuest={deleteGuest}
          />
        )}
        {activeTab === 'gifts' && !giftsLoading && (
          <GiftManagement
            gifts={gifts}
            onAddGift={addGift}
            onEditGift={editGift}
            onDeleteGift={deleteGift}
          />
        )}
        {activeTab === 'photos' && !photosLoading && (
          <PhotoManagement
            photos={photos}
            onDelete={deletePhoto}
          />
        )}
        {activeTab === 'messages' && !messagesLoading && (
          <MessageManagement
            messages={messages}
            onToggleApproval={toggleApproval}
            onDelete={deleteMessage}
            onApproveAll={approveAll}
          />
        )}
        {activeTab === 'homepage' && (
          <HomepageImageManagement
            images={images}
            isLoading={imagesLoading}
            error={imagesError}
            onAddImage={addImage}
            onUpdateImage={updateImage}
            onDeleteImage={deleteImage}
          />
        )}
        {activeTab === 'settings' && !settingsLoading && (
          <SettingsManagement
            photoAlbumUnlockDate={photoAlbumUnlockDate}
            onUnlock={unlockPhotoAlbum}
            onLock={lockPhotoAlbum}
          />
        )}
      </div>

      <AlertDialog
        isOpen={showAlert}
        title={alertTitle}
        message={alertMessage}
        onClose={clearAlert}
      />
    </div>
  );
};

export default AdminPage;